/* Independent hero. Native scroll; one video; no perpetual JS render loop. */
(() => {
  "use strict";
  const tag = "totoka-journey-hero-s26";
  if (customElements.get(tag)) return;
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const num = (v, f = 0) =>
    Number.isFinite(parseFloat(v)) ? parseFloat(v) : f;
  class JourneyHero extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.dead = false;
      this.visible = false;
      this.paused = false;
      this.frame = 0;
      this.revision = (this.revision || 0) + 1;
      this.failed = null;
      this.stage = this.querySelector("[data-stage]");
      this.copy = this.querySelector("[data-copy]");
      this.video = this.querySelector("video");
      this.control = this.querySelector("[data-motion-control]");
      this.mobile = matchMedia("(max-width:749px)");
      this.reduced = matchMedia("(prefers-reduced-motion:reduce)");
      this.layers = [...this.querySelectorAll("[data-layer]")];
      this.parallax = this.layers.filter(
        (el) => el.dataset.motion === "parallax",
      );
      this.onScroll = () => {
        if (this.running && this.parallax.length) this.schedule();
      };
      this.on(window, "resize", () => this.refresh(), { passive: true });
      this.on(this.mobile, "change", () => this.refresh());
      this.on(this.reduced, "change", () => this.refresh());
      this.on(document, "visibilitychange", () => this.syncMotion());
      this.on(document, "totoka:appearance-change", () => this.syncMotion());
      this.on(this.control, "click", () => {
        this.paused = !this.paused;
        this.syncMotion();
      });
      this.on(this.querySelector("[data-next]"), "click", () => this.goNext());
      this.on(this.video, "playing", () => {
        if (this.canPlay()) this.video.classList.add("is-playing");
        else this.video.pause();
      });
      this.on(this.video, "error", () => {
        this.video.classList.remove("is-playing");
        this.failed = this.source;
        this.pending = null;
        this.syncMotion();
      });
      this.on(document, "shopify:block:select", (event) => {
        const layer = this.layers.find(
          (el) => el.dataset.blockId === event.detail?.blockId,
        );
        if (layer) {
          this.classList.add("has-entered");
          layer.dataset.selected = "";
        }
      });
      this.on(document, "shopify:block:deselect", (event) => {
        this.layers
          .find((el) => el.dataset.blockId === event.detail?.blockId)
          ?.removeAttribute("data-selected");
      });
      if ("ResizeObserver" in window) {
        this.resize = new ResizeObserver(() => this.measure());
        this.resize.observe(this.stage);
        this.resize.observe(this.copy);
      }
      document.fonts?.ready.then(() => {
        if (!this.dead) this.measure();
      });
      this.classList.add("is-ready");
      this.refresh();
    }
    on(target, event, fn, options = {}) {
      target?.addEventListener(event, fn, {
        ...options,
        signal: this.abort.signal,
      });
    }
    findScrollRoot() {
      for (
        let el = this.parentElement;
        el && el !== document.body && el !== document.documentElement;
        el = el.parentElement
      ) {
        if (
          /^(auto|scroll|overlay)$/.test(getComputedStyle(el).overflowY) &&
          el.scrollHeight > el.clientHeight + 1
        )
          return el;
      }
      return null;
    }
    refresh() {
      const scrollRoot = this.findScrollRoot(),
        target = scrollRoot || window;
      if (this.scrollTarget !== target) {
        this.scrollTarget?.removeEventListener("scroll", this.onScroll);
        this.scrollTarget = target;
        this.scrollRoot = scrollRoot;
        this.on(target, "scroll", this.onScroll, { passive: true });
        this.observer?.disconnect();
        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              this.visible = entries[0].isIntersecting;
              if (this.visible) this.classList.add("has-entered");
              this.syncMotion();
            },
            { root: scrollRoot, threshold: 0 },
          );
          this.observer.observe(this.stage);
        } else {
          this.visible = true;
          this.classList.add("has-entered");
        }
      }
      this.classList.toggle("is-reduced", this.reduced.matches);
      this.measure();
      this.syncMotion();
    }
    measure() {
      if (this.dead) return;
      const css = getComputedStyle(this.copy),
        suffix = this.mobile.matches ? "-m" : "";
      const width = this.copy.offsetWidth,
        height = this.copy.offsetHeight;
      // Reserve enough space for large editor typography, then use the requested anchor.
      const min = height + 100;
      if (this.stage.style.getPropertyValue("--jh-content-min") !== `${min}px`)
        this.stage.style.setProperty("--jh-content-min", `${min}px`);
      const ax = num(css.getPropertyValue("--ax"), 0.5),
        ay = num(css.getPropertyValue("--ay"), 0.5);
      const x =
        (num(css.getPropertyValue(`--jh-x${suffix}`), 50) / 100) *
        this.stage.clientWidth;
      const y =
        (num(css.getPropertyValue(`--jh-y${suffix}`), 50) / 100) *
        this.stage.clientHeight;
      this.copy.style.setProperty(
        "--safe-x",
        `${clamp(x, 24 + width * ax, Math.max(24 + width * ax, this.stage.clientWidth - 24 - width * (1 - ax)))}px`,
      );
      this.copy.style.setProperty(
        "--safe-y",
        `${clamp(y, 24 + height * ay, Math.max(24 + height * ay, this.stage.clientHeight - 64 - height * (1 - ay)))}px`,
      );
      if (this.running) this.schedule();
    }
    darkBackdrop() { return this.dataset.darkBackground === "true" && document.documentElement.dataset.totokaAppearance === "dark"; }
    canPlay() {
      return (
        !this.dead &&
        this.visible &&
        !document.hidden &&
        !this.paused &&
        !this.reduced.matches &&
        this.dataset.background === "video" && !this.darkBackdrop()
      );
    }
    syncMotion() {
      if (this.dead) return;
      this.running =
        this.visible &&
        !document.hidden &&
        !this.paused &&
        !this.reduced.matches;
      this.classList.toggle("is-running", this.running);
      const hasMotion =
        (this.dataset.background === "video" && !this.darkBackdrop() &&
          Boolean(this.dataset.video || this.dataset.videoMobile)) ||
        this.layers.some((el) => el.dataset.motion !== "none");
      this.control.hidden = !hasMotion || this.reduced.matches;
      this.control.textContent = this.paused
        ? this.dataset.playLabel
        : this.dataset.pauseLabel;
      if (!this.running && this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
      }
      if (this.reduced.matches)
        this.parallax.forEach((el) => {
          el.style.setProperty("--px", "0px");
          el.style.setProperty("--py", "0px");
        });
      if (this.running && this.parallax.length) this.schedule();
      const wanted = this.mobile.matches
        ? this.dataset.videoMobile || this.dataset.video
        : this.dataset.video || this.dataset.videoMobile;
      if (this.source && this.source !== wanted) {
        this.revision++;
        this.video.pause();
        this.video.removeAttribute("src");
        this.video.load();
        this.video.classList.remove("is-playing");
        this.source = null;
        this.pending = null;
      }
      if (!this.canPlay() || !wanted) {
        this.video.pause();
        return;
      }
      if (this.failed === wanted) return;
      if (!this.source) {
        this.source = wanted;
        this.video.muted = true;
        this.video.src = wanted;
        this.video.load();
      }
      if (!this.video.paused || this.pending) return;
      const revision = this.revision;
      let interrupted = false;
      const pending = this.video.play();
      this.pending = pending;
      Promise.resolve(pending)
        .then(() => {
          if (this.dead || revision !== this.revision) return;
          if (!this.canPlay()) this.video.pause();
        })
        .catch((error) => {
          interrupted = error.name === "AbortError";
          if (
            error.name !== "AbortError" &&
            !this.dead &&
            revision === this.revision
          ) {
            this.paused = true;
            this.syncMotion();
          }
        })
        .finally(() => {
          if (this.pending !== pending) return;
          this.pending = null;
          // A quick offscreen/on-screen change can interrupt a pending play request.
          if (interrupted && revision === this.revision && this.canPlay())
            this.syncMotion();
        });
    }
    schedule() {
      if (this.frame || !this.running || !this.parallax.length) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (!this.running) return;
        const rect = this.stage.getBoundingClientRect(),
          top = this.scrollRoot
            ? this.scrollRoot.getBoundingClientRect().top
            : 0;
        const viewport = this.scrollRoot
          ? this.scrollRoot.clientHeight
          : innerHeight;
        const progress = clamp(
          (viewport / 2 - (rect.top - top + rect.height / 2)) /
            ((viewport + rect.height) / 2),
          -1,
          1,
        );
        this.parallax.forEach((el) => {
          const amount =
            num(
              this.mobile.matches
                ? el.dataset.parallaxMobile
                : el.dataset.parallax,
              30,
            ) * progress;
          el.style.setProperty(
            "--px",
            el.dataset.axis === "x" ? `${amount}px` : "0px",
          );
          el.style.setProperty(
            "--py",
            el.dataset.axis === "x" ? "0px" : `${amount}px`,
          );
        });
      });
    }
    goNext() {
      let next = this.closest(".shopify-section")?.nextElementSibling;
      while (next && !next.matches(".shopify-section"))
        next = next.nextElementSibling;
      if (!next) return;
      const top = this.scrollRoot
        ? this.scrollRoot.getBoundingClientRect().top
        : 0;
      const position = this.scrollRoot ? this.scrollRoot.scrollTop : scrollY;
      const header = num(
        getComputedStyle(this).getPropertyValue("--header-height"),
      );
      (this.scrollRoot || window).scrollTo({
        top: next.getBoundingClientRect().top - top + position - header,
        behavior: this.reduced.matches ? "instant" : "smooth",
      });
    }
    disconnectedCallback() {
      this.dead = true;
      this.revision++;
      this.abort?.abort();
      this.abort = null;
      this.observer?.disconnect();
      this.resize?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.video?.pause();
      this.video?.removeAttribute("src");
      this.video?.load();
      this.source = null;
      this.pending = null;
      this.scrollTarget = null;
      this.scrollRoot = null;
      this.running = false;
      this.video?.classList.remove("is-playing");
      this.classList.remove("is-running", "is-ready", "has-entered");
    }
  }
  customElements.define(tag, JourneyHero);
})();